import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { FormStepper, ValidationMessage } from "sj-ui-toolkit/src/components";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import {
  getKnowledgeAssessmentReportClient,
  getChoreReportClient,
  getChoresClient,
} from "sj-ui-toolkit/src/client";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";

import { loadAllLessons } from "@/api/lessons";
import { loadLicensesAsync } from "@/api/users";
import {
  addPerformanceCategorization,
  createCompleteAssignment,
  enrichLessonTree,
  getUniqueLessonKeys,
  removeLetterSuffix,
} from "@/api/recommendedAssignments";
import type { LearnerScore, RecommendedFormValues } from "@/api/recommendedAssignments";
import { calculateAverageScores, filterAllScores } from "@/helpers/assessmentsReport";
import type { FormValues, DirectoryUserShape, FormSettings } from "@/api/assignments";
import type { Lesson } from "@/api/lessons";
import { combinedLessonsForTree, SUBMISSION_STEPS } from "@/constants/recommendedAssignments";
import { useAvailableUsers } from "@/hooks/useAvailableUsers";

import { SubmitProgressDialog } from "@/components/assignments/SubmitProgressDialog";
import UserSelectionConfirmDialog from "@/components/assignments/UserSelectionConfirmDialog";
import AssignmentSettings from "@/components/assignments/AssignmentSettings";
import { IntroDialog } from "@/components/recommendedAssignment/IntroDialog";
import { LessonOrder } from "@/components/recommendedAssignment/LessonOrder";
import { PathCreation } from "@/components/recommendedAssignment/PathCreation";
import type { PathCreationValues } from "@/components/recommendedAssignment/PathCreation";
import RecommendedAudience from "@/components/recommendedAssignment/RecommendedAudience";
import RecommendedReview from "@/components/recommendedAssignment/RecommendedReview";

// ─── Types ────────────────────────────────────────────────────────────────────

interface RAFormValues {
  paths: PathCreationValues;
  orderedLessonKeys: string[];
  target: FormValues["target"];
  settings: FormSettings & { description?: string };
}

type ValidationError = { hasError: boolean; message: string };

interface RAValidationErrors {
  paths: ValidationError;
  audience: ValidationError;
  settings: {
    name: ValidationError;
    description: ValidationError;
    date: ValidationError;
  };
}

// ─── helpers ──────────────────────────────────────────────────────────────────

const getSessionItem = <T,>(key: string): T | null => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

const clearPrepopulated = () => {
  sessionStorage.removeItem("currentRecommendedAssignment");
  sessionStorage.removeItem("prepopulatedUserIds");
  sessionStorage.removeItem("assessmentScoreData");
};

// ─── Component ────────────────────────────────────────────────────────────────

const RecommendedAssignment = () => {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const tenantName = getCurrentSelectedTenant() ?? "";

  const { availableUsers: directoryUsers } = useAvailableUsers(tenantName);

  useBreadcrumb({
    label: "Recommended Assignment",
    route: `/admin/assess/recommend/${assessmentId}`,
    display: "page",
  });

  // ─── Loading ────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [isLoadingLessons, setIsLoadingLessons] = useState(true);
  const [scoresLoaded, setScoresLoaded] = useState(false);

  // ─── UI state ───────────────────────────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState(0);
  const [showValidationMessages, setShowValidationMessages] = useState(false);
  const [showIntroPopup, setShowIntroPopup] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [onlyStrengths, setOnlyStrengths] = useState(false);

  // ─── Form state ─────────────────────────────────────────────────────────────
  const [formValues, setFormValues] = useState<RAFormValues>({
    paths: { selectedPaths: {} },
    orderedLessonKeys: [],
    target: { availableUsers: [], users: [] },
    settings: {},
  });

  // ─── Validation ─────────────────────────────────────────────────────────────
  const [allAssignmentNames, setAllAssignmentNames] = useState<string[]>([]);
  const [isNameUnique, setIsNameUnique] = useState(true);
  const [validationErrors, setValidationErrors] = useState<RAValidationErrors>({
    paths: { hasError: true, message: "Please select at least one lesson" },
    audience: { hasError: true, message: "Please add at least one user" },
    settings: {
      name: { hasError: true, message: "Assignment name is required" },
      description: { hasError: true, message: "Assignment description is required" },
      date: { hasError: true, message: "Please select an end date" },
    },
  });

  // ─── Lesson tree ────────────────────────────────────────────────────────────
  const [rawLessonData, setRawLessonData] = useState<Lesson[]>([]);
  const [scoresByTopic, setScoresByTopic] = useState<
    Record<string, Array<{ topic: string; averageScore: number }>>
  >({});
  const [treeData, setTreeData] = useState<ReturnType<typeof addPerformanceCategorization>>([]);
  const [lessonKeysOffered, setLessonKeysOffered] = useState<Set<string>>(new Set());

  // ─── User filter sets ────────────────────────────────────────────────────────
  const [usersPrepopulated, setUsersPrepopulated] = useState(false);
  // Set synchronously before any awaits
  // Tells the Audience tab to start on "Users Added" once data is ready.
  const [hasPrepopulatedUsers, setHasPrepopulatedUsers] = useState(false);

  // ─── User selection dialog ───────────────────────────────────────────────────
  const [showUserSelectionDialog, setShowUserSelectionDialog] = useState(false);
  const [pendingStep, setPendingStep] = useState<number | null>(null);
  const [selectedUsersToAdd, setSelectedUsersToAdd] = useState<DirectoryUserShape[]>([]);
  const [selectedUsersToRemove, setSelectedUsersToRemove] = useState<DirectoryUserShape[]>([]);
  const addUsersAndContinueRef = useRef<(() => void) | null>(null);

  // ─── Submission ──────────────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccessful, setSubmissionSuccessful] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState<{
    value: number;
    label: string;
  } | null>(null);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  // ─── Derived ─────────────────────────────────────────────────────────────────
  const lessonMap = useMemo(() => {
    const map: Record<string, Lesson> = {};
    const collectLessons = (nodes: typeof treeData) => {
      for (const node of nodes) {
        if (node.lesson_key) map[String(node.lesson_key)] = node as Lesson;
        if (node.children?.length) collectLessons(node.children);
      }
    };
    collectLessons(treeData);
    return map;
  }, [treeData]);

  // ─── Session storage helpers ─────────────────────────────────────────────────
  const getPrepopulatedUsers = useCallback((): string[] | null => {
    const current = sessionStorage.getItem("currentRecommendedAssignment");
    if (current !== assessmentId) {
      setHasPrepopulatedUsers(false);
      return null;
    }
    const ids = getSessionItem<string[]>("prepopulatedUserIds");
    if (ids?.length) {
      setHasPrepopulatedUsers(true);
      return ids;
    }
    setHasPrepopulatedUsers(false);
    return null;
  }, [assessmentId]);

  const getPrepopulatedScoreData = useCallback(() => {
    const current = sessionStorage.getItem("currentRecommendedAssignment");
    if (current !== assessmentId) return null;
    return getSessionItem<{
      topicScores: RAFormValues["paths"]["selectedPaths"];
      learnerScores: LearnerScore[];
    }>("assessmentScoreData");
  }, [assessmentId]);

  // ─── Validation ──────────────────────────────────────────────────────────────
  const validateSection = useCallback(
    (
      section: string,
      values: RAFormValues["paths"] | RAFormValues["target"] | RAFormValues["settings"]
    ) => {
      switch (section) {
        case "paths": {
          const sp = (values as RAFormValues["paths"]).selectedPaths ?? {};
          const hasSelected = Object.values(sp).some(Boolean);
          setValidationErrors(prev => ({
            ...prev,
            paths: { hasError: !hasSelected, message: "Please select at least one lesson" },
          }));
          break;
        }
        case "target": {
          const users = (values as RAFormValues["target"]).users ?? [];
          setValidationErrors(prev => ({
            ...prev,
            audience: { hasError: users.length === 0, message: "Please add at least one user" },
          }));
          break;
        }
        case "settings": {
          const sv = values as RAFormValues["settings"];
          const nameError = !sv.name || sv.name.trim().length === 0;
          const descriptionError = !sv.description || sv.description.trim().length === 0;
          const dateError = !sv.dates?.dynamic && !sv.endDate;
          const nameExists = Boolean(sv.name && allAssignmentNames.includes(sv.name.toLowerCase()));
          setIsNameUnique(!nameExists);
          setValidationErrors(prev => ({
            ...prev,
            settings: {
              name: {
                hasError: nameError || nameExists,
                message: nameError
                  ? "Assignment name is required"
                  : nameExists
                    ? "An assignment with this name already exists"
                    : "",
              },
              description: {
                hasError: descriptionError,
                message: "Assignment description is required",
              },
              date: { hasError: dateError, message: "Please select an end date" },
            },
          }));
          break;
        }
        default:
          break;
      }
    },
    [allAssignmentNames]
  );

  const isFormValid = useMemo(
    () =>
      !validationErrors.paths.hasError &&
      !validationErrors.audience.hasError &&
      !validationErrors.settings.name.hasError &&
      !validationErrors.settings.description.hasError &&
      !validationErrors.settings.date.hasError &&
      isNameUnique,
    [validationErrors, isNameUnique]
  );

  // ─── handleFormChange ────────────────────────────────────────────────────────
  const handleFormChange = useCallback(
    (section: string, newValues: RAFormValues[keyof RAFormValues]) => {
      setFormValues(prev => ({ ...prev, [section]: newValues }));
      validateSection(
        section,
        newValues as RAFormValues["paths"] | RAFormValues["target"] | RAFormValues["settings"]
      );
    },
    [validateSection]
  );

  // ─── Lesson key reconciliation ────────────────────────────────────────────────
  const reconcileKeys = (
    oldKeys: string[],
    updatedKeys: Record<string, { checked: boolean; partialChecked: boolean }>,
    offered: Set<string>
  ): string[] => {
    const seenBaseKeys = new Set<string>();
    const checkedBaseKeys = new Set<string>();

    for (const [key, val] of Object.entries(updatedKeys)) {
      if (val.checked) {
        const base = removeLetterSuffix(key);
        if (offered.has(base)) checkedBaseKeys.add(base);
      }
    }

    const kept = oldKeys.filter(base => {
      const ok = offered.has(base) && checkedBaseKeys.has(base) && !seenBaseKeys.has(base);
      if (ok) seenBaseKeys.add(base);
      return ok;
    });

    const keptSet = new Set(kept);
    const added = [...checkedBaseKeys].filter(base => !keptSet.has(base));
    return [...kept, ...added];
  };

  const handleLessonChange = (newPaths: PathCreationValues) => {
    handleFormChange("paths", newPaths);
    const orderedKeys = reconcileKeys(
      formValues.orderedLessonKeys,
      newPaths.selectedPaths ?? {},
      lessonKeysOffered
    );
    handleFormChange("orderedLessonKeys", orderedKeys);
  };

  // ─── Step navigation ──────────────────────────────────────────────────────────
  const handleStepChange = (newStep: number) => {
    if (currentStep === 2 && newStep !== 2 && selectedUsersToAdd.length > 0) {
      setPendingStep(newStep);
      setShowUserSelectionDialog(true);
      return;
    }
    setCurrentStep(newStep);
  };

  // ─── Data fetching ────────────────────────────────────────────────────────────
  const fetchAndSetUsers = async () => {
    // Fetch is kept for potential future use; currently no-op
    void loadLicensesAsync().catch(() => undefined);
  };

  const fetchAssessment = async () => {
    if (!assessmentId || assessmentId === "self-initiated") return;
    try {
      const choresClient = getChoresClient();
      const { data, error } = await choresClient.getChore(assessmentId);
      if (error) throw error;
      if (!data) return;
      setFormValues(prev => ({
        ...prev,
        target: {
          ...prev.target,
          users: prev.target.users?.length ? prev.target.users : (data.target?.users ?? []),
        },
      }));
    } catch (err) {
      showToast({
        severity: "error",
        summary: "Error",
        detail: `Failed to fetch assessment data: ${String(err)}`,
      });
    }
  };

  const fetchAssessmentData = async () => {
    if (!assessmentId) return;

    const prepopScoreData = getPrepopulatedScoreData();
    const prepopUserIds = getPrepopulatedUsers();

    if (prepopScoreData && prepopUserIds && prepopUserIds.length > 0) {
      try {
        const { topicScores, learnerScores } = prepopScoreData as unknown as {
          topicScores: Record<string, Array<{ topic: string; averageScore: number }>>;
          learnerScores: LearnerScore[];
        };
        setScoresByTopic(topicScores);

        const response = await getKnowledgeAssessmentReportClient().getScores(tenantName, {
          assignmentId: assessmentId!,
        });
        if (response.error) throw response.error;
        const allScores = (
          (
            response.data as unknown as {
              assignmentScores?: Array<{ learnerScores?: LearnerScore[] }>;
            } | null
          )?.assignmentScores ?? []
        ).flatMap(d => d.learnerScores ?? []);

        const assignmentDetails = await getChoreReportClient().getStatus(tenantName, {}, true);
        if (assignmentDetails.error) throw assignmentDetails.error;

        const assignment = (assignmentDetails.data?.assignments ?? []).find(
          (a: { id: string }) => a.id === assessmentId
        );
        const overdueMap = new Map<string, boolean>(
          ((assignment?.userDetails ?? []) as Array<{ id: string; isOverdue: boolean }>).map(u => [
            u.id,
            u.isOverdue,
          ])
        );

        const allScoresWithOverdue = allScores.map((score: LearnerScore) => ({
          ...score,
          isOverdue: overdueMap.get(score.directoryUuid ?? "") ?? false,
        }));

        const alreadySelectedIds = new Set(
          (learnerScores as LearnerScore[]).map(u => u.directoryUuid)
        );
        const availableUsers = allScoresWithOverdue.filter(
          (u: LearnerScore) => !alreadySelectedIds.has(u.directoryUuid)
        );

        const updatedTarget = {
          availableUsers: availableUsers as unknown as DirectoryUserShape[],
          users: learnerScores as DirectoryUserShape[],
        };
        setFormValues(prev => ({ ...prev, target: { ...prev.target, ...updatedTarget } }));
        validateSection("target", updatedTarget);
        if ((learnerScores as LearnerScore[]).length > 0) setUsersPrepopulated(true);

        const hasOnlyStrengths =
          Object.keys(topicScores ?? {}).length > 0 &&
          Object.values(topicScores ?? {}).every(
            arr => arr.length > 0 && arr.every(item => item.averageScore > 80)
          );
        setOnlyStrengths(hasOnlyStrengths);
        setScoresLoaded(true);
        return;
      } catch {
        // fall through to API fetch
      }
    }

    if (assessmentId === "self-initiated" || formValues.target.availableUsers?.length) {
      setScoresLoaded(true);
      return;
    }

    try {
      const response = await getKnowledgeAssessmentReportClient().getScores(tenantName, {
        assignmentId: assessmentId!,
      });
      if (response.error) throw response.error;
      const scores = (
        (
          response.data as unknown as {
            assignmentScores?: Array<{ learnerScores?: LearnerScore[] }>;
          } | null
        )?.assignmentScores ?? []
      ).flatMap(d => d.learnerScores ?? []);

      const assignmentDetails2 = await getChoreReportClient().getStatus(tenantName, {}, true);
      if (assignmentDetails2.error) throw assignmentDetails2.error;

      const assignment2 = (assignmentDetails2.data?.assignments ?? []).find(
        (a: { id: string }) => a.id === assessmentId
      );
      const overdueMap = new Map<string, boolean>(
        ((assignment2?.userDetails ?? []) as Array<{ id: string; isOverdue: boolean }>).map(u => [
          u.id,
          u.isOverdue,
        ])
      );

      const scoresWithOverdue = scores.map((score: LearnerScore) => ({
        ...score,
        isOverdue: overdueMap.get(score.directoryUuid ?? "") ?? false,
      }));

      const prepopIds = prepopUserIds ?? [];
      const alreadySelected = prepopIds.length
        ? scoresWithOverdue.filter((u: LearnerScore) => prepopIds.includes(u.directoryUuid ?? ""))
        : scoresWithOverdue;

      const updatedTarget = {
        availableUsers: (prepopIds.length
          ? []
          : scoresWithOverdue) as unknown as DirectoryUserShape[],
        users: prepopIds.length ? (alreadySelected as DirectoryUserShape[]) : [],
      };
      setFormValues(prev => ({ ...prev, target: { ...prev.target, ...updatedTarget } }));

      if (prepopIds.length) {
        validateSection("target", updatedTarget);
        setUsersPrepopulated(true);
      }

      const { topicScoresAverage } = calculateAverageScores(filterAllScores(alreadySelected));
      setScoresByTopic(topicScoresAverage);

      const hasOnlyStrengths =
        Object.keys(topicScoresAverage).length > 0 &&
        Object.values(topicScoresAverage).every(
          arr => arr.length > 0 && arr.every(item => item.averageScore > 80)
        );
      setOnlyStrengths(hasOnlyStrengths);
      setScoresLoaded(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      showToast({
        severity: "error",
        summary: "Error",
        detail: `Failed to fetch audience data: ${msg}`,
      });
    }
  };

  const fetchAllAssignments = async () => {
    try {
      const assignmentsClient = (await import("sj-ui-toolkit/src/client")).getAssignmentsClient();
      const firstPage = await assignmentsClient.assignments(tenantName, { page: 1, limit: 0 });
      if (firstPage.error) throw firstPage.error;
      const totalPages = firstPage.data?.totalPages ?? 1;
      const firstPageItems = firstPage.data?.assignments ?? [];
      const rest =
        totalPages > 1
          ? await Promise.all(
              Array.from({ length: totalPages - 1 }, (_, i) =>
                assignmentsClient
                  .assignments(tenantName, { page: i + 2, limit: 0 })
                  .then(r => r.data?.assignments ?? [])
                  .catch(() => [])
              )
            )
          : [];
      const allNames = [firstPageItems, ...rest]
        .flat()
        .map((a: { name?: string }) => a.name?.toLowerCase() ?? "");
      setAllAssignmentNames(allNames);
    } catch {
      // non-critical for submission
    }
  };

  const fetchLessonData = async () => {
    const { error, lessons } = await loadAllLessons();
    if (error) {
      showToast({
        severity: "error",
        summary: "Error",
        detail: `Failed to fetch lessons: ${error}`,
      });
    } else {
      setRawLessonData(lessons);
    }
  };

  // ─── Effects ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    void fetchAssessmentData();
    void fetchLessonData();
    if (assessmentId && assessmentId !== "self-initiated") void fetchAssessment();
    void fetchAllAssignments();
    void fetchAndSetUsers();
    setLoading(false);
  }, [assessmentId]);

  // Re-run name validation once the assignment list arrives. validateSection closes over
  // allAssignmentNames via useCallback, so if the user typed a name before the fetch
  // resolved the duplicate check ran against [] and was never retried.
  useEffect(() => {
    if (allAssignmentNames.length === 0 || !formValues.settings.name) return;
    validateSection("settings", formValues.settings);
  }, [allAssignmentNames]);

  useEffect(() => {
    if (rawLessonData.length > 0 && Object.keys(scoresByTopic).length > 0) {
      const enriched = enrichLessonTree(combinedLessonsForTree, rawLessonData);
      const withCats = addPerformanceCategorization(enriched, scoresByTopic);
      setTreeData(withCats);
      setLessonKeysOffered(getUniqueLessonKeys(withCats));
      setIsLoadingLessons(false);
    }
  }, [rawLessonData, scoresByTopic]);

  useEffect(() => {
    const stored = localStorage.getItem("dontShowRecommendedAssignmentIntroAgain");
    if (!stored || stored === "false") setShowIntroPopup(true);
  }, []);

  useEffect(() => {
    if (currentStep === steps.length - 1) setShowValidationMessages(true);
  }, [currentStep]);

  // ─── Pre-populate users ────────────────────────────────────────────────────
  useEffect(() => {
    const prepopIds = getPrepopulatedUsers();
    if (
      prepopIds &&
      scoresLoaded &&
      (formValues.target.availableUsers?.length ?? 0) > 0 &&
      !usersPrepopulated
    ) {
      const toAdd = (formValues.target.availableUsers ?? []).filter(u =>
        prepopIds.includes(u.directoryUuid ?? "")
      );
      if (toAdd.length > 0) {
        const currentIds = new Set((formValues.target.users ?? []).map(u => u.directoryUuid));
        const newUsers = toAdd.filter(u => !currentIds.has(u.directoryUuid));
        if (newUsers.length > 0) {
          const updatedTarget = {
            ...formValues.target,
            users: [...(formValues.target.users ?? []), ...(newUsers as DirectoryUserShape[])],
            availableUsers: (formValues.target.availableUsers ?? []).filter(
              u => !prepopIds.includes(u.directoryUuid ?? "")
            ),
          };
          setFormValues(prev => ({ ...prev, target: updatedTarget }));
          validateSection("target", updatedTarget);
          setUsersPrepopulated(true);
        }
      }
    }
  }, [scoresLoaded]);

  // Cleanup session storage on unmount
  useEffect(() => {
    return () => clearPrepopulated();
  }, []);

  // ─── Handlers ────────────────────────────────────────────────────────────────
  const handleDontShowIntroAgain = (v: boolean) => {
    localStorage.setItem("dontShowRecommendedAssignmentIntroAgain", String(v));
    setDontShowAgain(v);
  };

  const handleSelectedUsersChange = (users: DirectoryUserShape[]) => setSelectedUsersToAdd(users);

  const handleContinue = () => {
    setShowUserSelectionDialog(false);
    if (pendingStep !== null) setCurrentStep(pendingStep);
    setPendingStep(null);
  };

  const handleAddUsersAndContinue = () => {
    addUsersAndContinueRef.current?.();
    setShowUserSelectionDialog(false);
    if (pendingStep !== null) setCurrentStep(pendingStep);
    setPendingStep(null);
  };

  const handleCancel = () => {
    confirmDialog({
      message: "Changes you made will not be saved.",
      header: "Are you sure?",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      icon: "pi pi-exclamation-circle",
      acceptLabel: "Confirm",
      rejectLabel: "Cancel",
      headerStyle: { border: "none" },
      accept: () => navigate(AdminAppRoutes.routes.assignments.route),
    });
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setSubmissionProgress(SUBMISSION_STEPS.VALIDATING);
    setSubmissionError(null);
    try {
      await createCompleteAssignment(
        {
          orderedLessonKeys: formValues.orderedLessonKeys,
          target: formValues.target as RecommendedFormValues["target"],
          settings: formValues.settings,
        },
        lessonMap,
        progress => setSubmissionProgress(progress)
      );
      showToast({
        severity: "success",
        summary: "Success",
        detail: "Assignment created successfully",
      });
      setSubmissionSuccessful(true);
      clearPrepopulated();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to create assignment";
      setSubmissionError(msg);
      showToast({ severity: "error", summary: "Error", detail: msg });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Steps definition ─────────────────────────────────────────────────────────
  const steps = [
    {
      header: showValidationMessages
        ? `Path Creation${!validationErrors.paths.hasError ? " ✓" : " ✗"}`
        : "Path Creation",
      title: "Path Creation",
      content: (
        <PathCreation
          values={formValues.paths}
          treeData={treeData}
          lessonOrder={formValues.orderedLessonKeys}
          handleChange={handleLessonChange}
          showValidationMessages={showValidationMessages}
          validationErrors={validationErrors.paths}
          isLoading={isLoadingLessons}
          lessonMap={lessonMap}
        />
      ),
    },
    {
      header: showValidationMessages
        ? `Path Order${!validationErrors.paths.hasError ? " ✓" : " ✗"}`
        : "Path Order",
      title: "Path Order",
      content: (
        <LessonOrder
          lessonMap={lessonMap}
          lessonOrder={formValues.orderedLessonKeys}
          handleChange={keys => handleFormChange("orderedLessonKeys", keys)}
        />
      ),
    },
    {
      header: showValidationMessages
        ? `Audience${!validationErrors.audience.hasError ? " ✓" : " ✗"}`
        : "Audience",
      title: "User Selection",
      content: (
        <RecommendedAudience
          key={usersPrepopulated ? 1 : 0}
          values={formValues.target}
          handleChange={v => handleFormChange("target", v)}
          onSelectedUsersChange={handleSelectedUsersChange}
          onAddUsersAndContinue={addUsersAndContinueRef}
          selectedUsersToAdd={selectedUsersToAdd}
          setSelectedUsersToAdd={setSelectedUsersToAdd}
          selectedUsersToRemove={selectedUsersToRemove}
          setSelectedUsersToRemove={setSelectedUsersToRemove}
          hasPrepopulatedUsers={hasPrepopulatedUsers}
          directoryUsers={directoryUsers}
        />
      ),
    },
    {
      header: showValidationMessages
        ? `Assignment Settings${
            !(
              validationErrors.settings.name.hasError ||
              validationErrors.settings.description.hasError ||
              validationErrors.settings.date.hasError
            )
              ? " ✓"
              : " ✗"
          }`
        : "Assignment Settings",
      title: "Assignment Settings",
      content: (
        <AssignmentSettings
          values={formValues.settings}
          handleChange={v => handleFormChange("settings", v)}
          showValidationMessages={showValidationMessages}
          validationErrors={validationErrors.settings}
        />
      ),
    },
    {
      header: "Review",
      title: "Review Assignment",
      content: (
        <>
          <RecommendedReview
            values={{
              orderedLessonKeys: formValues.orderedLessonKeys,
              target: formValues.target,
              settings: formValues.settings,
            }}
            onStepChange={setCurrentStep}
            validationErrors={validationErrors}
            isNameUnique={isNameUnique}
            lessonMap={lessonMap}
          />
          {!isFormValid && (
            <div className="flex justify-end mt-4">
              <ValidationMessage
                severity="warn"
                text="Please fill in all required fields before submitting"
              />
            </div>
          )}
        </>
      ),
    },
  ];

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Page header */}
      <div className="flex items-center justify-between mb-4">
        <h1>Create Recommended Assignment</h1>
        <Button
          icon="pi pi-times"
          onClick={handleCancel}
          severity="contrast"
          aria-label="Cancel and go back to assignments"
          data-testid="recommended-assignment-cancel-button"
        />
      </div>

      <div
        className={`p-4 min-h-[60vh] flex flex-col ${themeClasses.bgElevation1}`}
        data-testid="recommended-assignment-container"
      >
        {loading ? (
          <div className="flex justify-center py-8">
            <span className="pi pi-spinner pi-spin" style={{ fontSize: "2rem" }} />
          </div>
        ) : (
          <FormStepper
            steps={steps}
            currentStep={currentStep}
            setCurrentStep={handleStepChange}
            handleSubmit={() => void handleSubmit()}
            isNew
            isSubmitting={isSubmitting}
            submitButtonText="Submit"
            submitDisabled={!isFormValid || isSubmitting}
            data-testid="recommended-assignment-form-stepper"
          />
        )}
      </div>

      <ConfirmDialog maskStyle={{ backdropFilter: "blur(5px)" }} />

      <UserSelectionConfirmDialog
        visible={showUserSelectionDialog}
        onHide={handleContinue}
        onContinue={handleContinue}
        onAddUsersAndContinue={handleAddUsersAndContinue}
        selectedUsersCount={selectedUsersToAdd.length}
      />

      <SubmitProgressDialog
        isSubmitting={isSubmitting}
        submissionSuccessful={submissionSuccessful}
        submissionError={submissionError}
        submissionProgress={submissionProgress}
        onHide={() => {
          setSubmissionSuccessful(false);
          setSubmissionError(null);
        }}
        onNavigateTo={() => navigate(AdminAppRoutes.routes.assignments.route)}
        successButtonLabel="Go to Assignments"
        successMessage="Your assignment has been successfully created!"
      />

      <IntroDialog
        visible={showIntroPopup}
        onHide={() => setShowIntroPopup(false)}
        dontShowAgain={dontShowAgain}
        onDontShowAgainChange={handleDontShowIntroAgain}
        title="Your Recommended Assignment"
        message="Security Journey has reviewed your Assessment results and has pre-selected one lesson per topic, based on your team's scores. Feel free to add lessons in any topic area, remove some, or assign it as is."
        buttonLabel="Next"
        onlyStrengths={onlyStrengths}
      />
    </>
  );
};

export default RecommendedAssignment;
